import { useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppContext } from "../contexts";

import { getCompanyProfileInfo, getCompanyProfileList } from "../store/actions/companyActions";
import { COMPANY_SUPERADMIN } from "../constants/company";
import { getUserInfo } from "../store/actions/authActions";

let hasFetchedProfiles = false;
let storedCompanyId = "";
let fetchProfilePermissions = false;

const useCompanyProfiles = ({ profileId, profileName, defaultLoad } = {}) => {
  const dispatch = useDispatch();
  const {
    profileList: userProfiles,
    totalProfiles,
    profilePermissionInfo,
    profileLoading,
    fetchingProfileInfo,
  } = useSelector(state => state?.company);
  const currentUserInfo = useSelector(state => state.auth?.user);
  const { browserLoadType } = useContext(AppContext);

  const [companyProfiles, setCompanyProfiles] = useState();
  const [companyProfilePermissions, setCompanyProfilePermissions] = useState();
  const [profileInfo, setProfileInfo] = useState();
  const excludeArray = [];

  useEffect(() => {
    if (profileId && !profilePermissionInfo && !defaultLoad) {
      dispatch(getCompanyProfileInfo(profileId));
    }
  }, [profileId]);

  useEffect(() => {
    if (profilePermissionInfo) {
      setCompanyProfilePermissions(profilePermissionInfo?.config);
    }
  }, [profilePermissionInfo]);

  useEffect(() => {
    if (currentUserInfo && currentUserInfo.role === COMPANY_SUPERADMIN) {
      if (userProfiles) {
        const updatedProfiles = userProfiles.map(({ _id, ...rest }) => ({
          id: _id,
          slug: rest.name?.toLowerCase().replace(/\s+/g, "_"),
          ...rest,
        }));
        setCompanyProfiles(updatedProfiles);
        if (profileId) {
          setProfileInfo(updatedProfiles.find(resp => resp.id === profileId));
        }
      } else if (!profileLoading && !hasFetchedProfiles) {
        hasFetchedProfiles = true; // mark as fetched
        storedCompanyId = currentUserInfo?.company?.id;
        dispatch(getCompanyProfileList());
      }
    }

    if (!currentUserInfo || (currentUserInfo?.company?.id !== storedCompanyId && storedCompanyId != "")) {
      hasFetchedProfiles = false;
      fetchProfilePermissions = false;
    }
  }, [userProfiles, currentUserInfo]);

  // Below code to load user info if restored tha closed tab
  useLayoutEffect(() => {
    if (browserLoadType === "restore") {
      dispatch(getUserInfo()).then(resp => {
        dispatch(getCompanyProfileInfo(resp.userInfo.profile._id));
      });
    }
  }, [browserLoadType]);

  // Check profilePermissionInfo and fetchingProfileInfo is not loading. this is to reduce multiple profile api call
  useLayoutEffect(() => {
    if (
      currentUserInfo?.profile &&
      defaultLoad &&
      !profilePermissionInfo & !fetchingProfileInfo &&
      !fetchProfilePermissions
    ) {
      fetchProfilePermissions = true;
      dispatch(getCompanyProfileInfo(currentUserInfo?.profile?._id));
    }
  }, [currentUserInfo]);

  useLayoutEffect(() => {
    if (profileName) {
      setProfileInfo({
        id: profileId,
        name: profileName,
        slug: profileName.name?.toLowerCase().replace(/\s+/g, "_"),
      });
    }
  }, [profileName]);

  const generateProfilePermissions = useMemo(() => {
    if (!companyProfilePermissions || !profileInfo) return;

    // Check if profilePermissions are already in localStorage
    // const storedPermissions = localStorage.getItem(`profilePermissions_${profileId}`);
    // if (storedPermissions) {
    //   return JSON.parse(storedPermissions);
    // }

    // If not in localStorage, generate and store it
    const permissions = companyProfilePermissions?.map(profileGroup => {
      // Determine if this group should be enabled based on the profile
      let isEnabled = false;
      let profileName = profileInfo?.name?.toLowerCase().replace(/\s+/g, "_");

      if (profileName === "super_admin") {
        isEnabled = true; // Enable all permissions
      } else if (profileName === "admin") {
        isEnabled = profileGroup.permission_slug !== "super_admin_permissions"; // Disable Super Admin Permissions
      } else if (profileName === "standard") {
        isEnabled = profileGroup.permission_slug === "general_permissions"; // Only General Permissions enabled
      }

      // Map the permissions and add the enabled field
      return {
        ...profileGroup,
        permissions: profileGroup.permissions
          .filter(permission => !excludeArray.includes(permission.module)) // Exclude specified modules
          .map(permission => ({
            ...permission,
            enabled: isEnabled,
          })),
      };
    });

    // Store in localStorage
    localStorage.setItem(`profilePermissions_${profileId}`, JSON.stringify(permissions));

    return permissions;
  }, [profileInfo, companyProfilePermissions, profileId]);

  return {
    profilePermissions: generateProfilePermissions,
    companyProfiles: companyProfiles,
    profileInfo: profileInfo,
    totalProfiles: totalProfiles,
  };
};

export default useCompanyProfiles;
